import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, Save, Edit3, Trash2, Building2, FileText, Package,
  BookOpen, Mail, Phone, Globe, MapPin, Tag, Upload, Download, X, Boxes,
  AlertCircle, CheckCircle2,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import { useAuth } from "../context/AuthContext";
import { BACKEND_URL } from "../lib/api";
import { SupplierPricelistUploader } from "../components/SupplierPricelistImporter";
import DictTextarea from "../components/DictTextarea";

// Iter 402.1 → 411 (Dirk, 2026-02-28) — Rich OCR status badge for
// supplier docs. Mirrors the customer sales-doc badge (AccountBlock.jsx)
// so operators get the SAME safety-net signals on both sides:
//   • Scanning (with elapsed timer + Re-scan escape hatch after 90s)
//   • OCR failed (with reason + retry)
//   • RECONCILED · N lines · Xm ago (green, computed sum matches subtotal)
//   • OCR REVIEW NEEDED · Δ R xxx (red banner, sum ≠ subtotal → warehouse-blocker)
//   • Scan-drift ↑↓ vs previous count when a re-scan produced a different
//     row count on the same subtotal.
function OcrBadge({ doc, supplierId, onRefresh }) {
  const initial = doc?.ocr_status || null;
  const [status, setStatus] = useState(initial);
  const [error, setError] = useState(doc?.ocr_error || null);
  const [reconciliation, setReconciliation] = useState(doc?.ocr_reconciliation || null);
  const [busy, setBusy] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());

  // Elapsed-time ticker for the "Scanning…" strip.
  useEffect(() => {
    if (status !== "pending" && status !== "running") return undefined;
    const iv = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [status]);

  // Treat pending/running as silently complete if the doc is more
  // than 10 min old (the BG worker forgot to flip status).
  const ageMs = (() => {
    const ts = doc?.created_at;
    if (!ts) return Infinity;
    try {
      const t = new Date(ts).getTime();
      return Number.isFinite(t) ? Date.now() - t : Infinity;
    } catch { return Infinity; }
  })();
  const STALE_AGE_MS = 10 * 60 * 1000;
  const isStaleInFlight = (status === "pending" || status === "running") && ageMs > STALE_AGE_MS;

  // Poll while OCR is in flight (max 5 min).
  useEffect(() => {
    if (status !== "pending" && status !== "running") return undefined;
    if (isStaleInFlight) return undefined;
    let cancelled = false;
    const start = Date.now();
    const tick = async () => {
      if (cancelled) return;
      if (Date.now() - start > 5 * 60 * 1000) {
        setStatus("error");
        setError("Timed out waiting for OCR.");
        return;
      }
      try {
        const { data } = await api.get(`/suppliers/${supplierId}/documents/${doc.id}`);
        if (cancelled) return;
        setStatus(data.ocr_status || null);
        setError(data.ocr_error || null);
        setReconciliation(data.ocr_reconciliation || null);
        if (data.ocr_status !== "pending" && data.ocr_status !== "running") return;
      } catch { /* silent */ }
      if (!cancelled) setTimeout(tick, 4000);
    };
    const t = setTimeout(tick, 4000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [status, doc?.id, supplierId, isStaleInFlight]);

  // Silent re-run detection while ready (reaper can re-fire on pod crash).
  useEffect(() => {
    if (status !== "ready") return undefined;
    let cancelled = false;
    let ticks = 0;
    const iv = setInterval(async () => {
      if (cancelled) { clearInterval(iv); return; }
      ticks += 1;
      if (ticks > 10) { clearInterval(iv); return; }
      try {
        const { data } = await api.get(`/suppliers/${supplierId}/documents/${doc.id}`);
        if (cancelled) return;
        if (data.ocr_status !== status) setStatus(data.ocr_status || null);
        setReconciliation(data.ocr_reconciliation || null);
        setError(data.ocr_error || null);
      } catch { /* silent */ }
    }, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [status, doc?.id, supplierId]);

  const rescan = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setBusy(true);
    try {
      await api.post(`/suppliers/${supplierId}/documents/${doc.id}/rescan-ocr`);
      setStatus("pending");
      setError(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Re-scan failed.");
    } finally { setBusy(false); }
  };

  if (!status || status === "none" || isStaleInFlight) return null;

  // Mismatch banner — warehouse blocker. Delta > R 5 AND > 2%.
  if (status === "ready" && reconciliation?.status === "mismatch") {
    const delta = reconciliation.delta;
    return (
      <div className="inline-flex flex-col gap-1 max-w-full"
           data-testid={`supplier-doc-ocr-recon-mismatch-${doc.id}`}>
        <span className="text-[9px] font-mono uppercase tracking-wider border px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-200 border-rose-400/50 inline-flex items-center gap-1 self-start">
          <AlertCircle className="w-2.5 h-2.5 shrink-0" />
          OCR review needed · Δ R {Math.abs(delta || 0).toLocaleString("en-GB", { maximumFractionDigits: 2 })}
          <button type="button" onClick={rescan} disabled={busy}
                  className="hover:text-white text-[9px] underline disabled:opacity-50 ml-1"
                  data-testid={`supplier-doc-ocr-recon-rescan-${doc.id}`}>
            {busy ? "…" : "Re-scan"}
          </button>
        </span>
        <span className="text-[10px] text-rose-200/80 leading-tight break-words max-w-[320px]">
          {reconciliation.note ||
            `Line items sum (R ${(reconciliation.computed_lines_total || 0).toLocaleString("en-GB")}) does not match extracted subtotal (R ${(reconciliation.extracted_subtotal || 0).toLocaleString("en-GB")}).`}
        </span>
      </div>
    );
  }

  // Ready — Reconciled / No-subtotal / Scanned with drift indicator.
  if (status === "ready") {
    const completedMs = (() => {
      const ts = doc?.ocr_completed_at || doc?.updated_at;
      if (!ts) return null;
      try { const t = new Date(ts).getTime(); return Number.isFinite(t) ? t : null; } catch { return null; }
    })();
    const agoS = completedMs ? Math.max(0, Math.floor((nowTick - completedMs) / 1000)) : null;
    const agoLabel = agoS == null ? "" :
      agoS < 60 ? `${agoS}s ago` :
      agoS < 3600 ? `${Math.floor(agoS / 60)}m ago` :
      agoS < 86400 ? `${Math.floor(agoS / 3600)}h ago` :
      `${Math.floor(agoS / 86400)}d ago`;
    const lineCount = reconciliation?.line_count ?? (Array.isArray(doc?.line_items) ? doc.line_items.length : null);
    const prevCount = doc?.line_count_previous;
    const drift = doc?.line_count_delta;
    const isReconciled = reconciliation?.status === "match";
    const noSubtotal = reconciliation?.status === "no_subtotal";
    return (
      <span
        className={`text-[9px] font-mono uppercase tracking-wider border px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${
          isReconciled
            ? "bg-emerald-500/10 text-emerald-300 border-emerald-400/30"
            : "bg-white/5 text-[#a0aabf] border-white/10"
        }`}
        title={
          isReconciled
            ? `Line items sum (R ${(reconciliation.computed_lines_total || 0).toLocaleString("en-GB")}) matches the extracted subtotal — safe to push to procurement.`
            : noSubtotal
            ? "OCR did not extract a subtotal to reconcile against — manually verify the line items sum before tracking."
            : `Scanned ${agoLabel}. ${lineCount ?? "?"} lines extracted.`
        }
        data-testid={`supplier-doc-ocr-ready-${doc.id}`}
      >
        {isReconciled
          ? <CheckCircle2 className="w-2.5 h-2.5 text-emerald-400" />
          : <AlertCircle className="w-2.5 h-2.5 text-[#a0aabf]" />}
        {isReconciled ? "Reconciled" : noSubtotal ? "No subtotal" : "Scanned"}
        {lineCount != null && <span className="text-[#647087]"> · {lineCount}L</span>}
        {typeof drift === "number" && drift !== 0 && typeof prevCount === "number" && (
          <span className={`ml-1 font-mono text-[9px] ${drift > 0 ? "text-emerald-300" : "text-amber-300"}`}
                title={`Previous scan produced ${prevCount} lines. OCR is non-deterministic — visually confirm the row count against the source PDF.`}>
            {drift > 0 ? `↑${drift}` : drift} vs {prevCount}L
          </span>
        )}
        {agoLabel && <span className="text-[#647087]"> · {agoLabel}</span>}
        <button type="button" onClick={rescan} disabled={busy}
                className="hover:text-white underline disabled:opacity-50 ml-1"
                data-testid={`supplier-doc-ocr-refresh-${doc.id}`}
                title="Force a fresh OCR pass">
          {busy ? "…" : "Re-scan"}
        </button>
      </span>
    );
  }

  if (status === "error") {
    return (
      <div className="inline-flex flex-col gap-1 max-w-full"
           data-testid={`supplier-doc-ocr-error-${doc.id}`}>
        <span className="text-[9px] font-mono uppercase tracking-wider border px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border-rose-400/40 inline-flex items-center gap-1 self-start">
          OCR failed
          <button type="button" onClick={rescan} disabled={busy}
                  className="hover:text-white text-[9px] underline disabled:opacity-50"
                  data-testid={`supplier-doc-ocr-rescan-${doc.id}`}>
            {busy ? "…" : "Re-scan"}
          </button>
        </span>
        {error && (
          <span className="text-[10px] text-rose-300/80 leading-tight break-words max-w-[280px]"
                data-testid={`supplier-doc-ocr-error-msg-${doc.id}`}>
            {error}
          </span>
        )}
      </div>
    );
  }

  // pending / running
  const startedMs = (() => {
    const ts = doc?.ocr_started_at || doc?.created_at;
    if (!ts) return null;
    try { const t = new Date(ts).getTime(); return Number.isFinite(t) ? t : null; } catch { return null; }
  })();
  const elapsedS = startedMs ? Math.max(0, Math.floor((nowTick - startedMs) / 1000)) : null;
  const elapsedLabel = elapsedS == null ? "" :
    elapsedS < 60 ? `${elapsedS}s` :
    `${Math.floor(elapsedS / 60)}m ${elapsedS % 60}s`;
  const stalled = elapsedS != null && elapsedS >= 90;
  return (
    <span
      className={`text-[9px] font-mono uppercase tracking-wider border px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${
        stalled
          ? "bg-amber-500/15 text-amber-200 border-amber-400/50"
          : "bg-[#A78BFA]/15 text-[#A78BFA] border-[#A78BFA]/40"
      }`}
      title={stalled
        ? "OCR is taking longer than usual — click Re-scan to retry."
        : `OCR is running. Started ${elapsedLabel || "just now"}.`}
      data-testid={`supplier-doc-ocr-pending-${doc.id}`}
    >
      <Loader2 className="w-2.5 h-2.5 animate-spin" /> Scanning{elapsedLabel ? ` · ${elapsedLabel}` : "…"}
      {stalled && (
        <button type="button" onClick={rescan} disabled={busy}
                className="hover:text-white underline disabled:opacity-50 ml-1"
                data-testid={`supplier-doc-ocr-restart-${doc.id}`}>
          {busy ? "…" : "Re-scan"}
        </button>
      )}
    </span>
  );
}

// Iter 402.2 — one-click "Send OCR'd PO to a Procurement Tracker".
// Loads existing draft/ordered/partial trackers for this supplier, lets
// the user pick or create a new one, then hits the pull endpoint.
function SendToTrackerButton({ supplier, doc }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [trackers, setTrackers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(null);

  async function toggle() {
    if (open) { setOpen(false); return; }
    setLoading(true);
    setOpen(true);
    try {
      const { data } = await api.get("/procurement/trackers", {
        params: { supplier_id: supplier.id, limit: 20 },
      });
      setTrackers((data?.items || []).filter(
        (t) => !["received", "closed", "cancelled"].includes(t.status),
      ));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed.");
    } finally { setLoading(false); }
  }

  async function sendTo(tid) {
    setSending(tid);
    try {
      await api.post(`/procurement/trackers/${tid}/pull-from-supplier-doc`, {
        supplier_doc_id: doc.id,
      });
      toast.success("Pulled into procurement tracker.");
      navigate(`/procurement/${tid}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed.");
      setSending(null);
    }
  }

  async function createAndSend() {
    setSending("new");
    try {
      const { data } = await api.post("/procurement/trackers", {
        supplier_id: supplier.id,
        name: `PO tracker · ${doc.document_number || doc.name}`,
        source_supplier_doc_ids: [doc.id],
      });
      await api.post(`/procurement/trackers/${data.id}/pull-from-supplier-doc`, {
        supplier_doc_id: doc.id,
      });
      toast.success("Tracker created & pulled.");
      navigate(`/procurement/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed.");
      setSending(null);
    }
  }

  return (
    <div className="relative">
      <button onClick={toggle}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#1FB6C1]/30 bg-[#1FB6C1]/[0.06] text-[#1FB6C1] hover:bg-[#1FB6C1]/[0.15] text-[10px] font-medium uppercase tracking-wider"
              title="Start tracking this document in Procurement — creates a new tracker for this supplier and pulls in the line items"
              data-testid={`supplier-doc-send-tracker-${doc.id}`}>
        <Boxes className="w-3.5 h-3.5" />
        <span>Track</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 w-64 hv-card p-2 space-y-1"
             data-testid={`supplier-doc-tracker-menu-${doc.id}`}>
          <div className="text-[10px] uppercase tracking-wider text-[#647087] px-1 pb-1 border-b border-white/5">
            Send to tracker
          </div>
          {loading ? (
            <div className="text-[11px] text-[#a0aabf] px-1 py-2 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              {trackers.length === 0 ? (
                <div className="text-[11px] text-[#647087] px-1 py-1">
                  No open trackers for this supplier.
                </div>
              ) : trackers.map((t) => (
                <button key={t.id} onClick={() => sendTo(t.id)}
                        disabled={!!sending}
                        className="w-full text-left px-2 py-1 rounded hover:bg-white/5 text-[11px] flex items-center gap-1"
                        data-testid={`supplier-doc-tracker-opt-${t.id}`}>
                  <span className="flex-1 truncate">{t.name}</span>
                  {sending === t.id && <Loader2 className="w-3 h-3 animate-spin" />}
                </button>
              ))}
              <button onClick={createAndSend} disabled={!!sending}
                      className="w-full text-left px-2 py-1 rounded bg-[#1FB6C1]/10 hover:bg-[#1FB6C1]/20 text-[#1FB6C1] text-[11px] flex items-center gap-1 mt-1"
                      data-testid={`supplier-doc-tracker-new-${doc.id}`}>
                <span className="flex-1">+ New tracker</span>
                {sending === "new" && <Loader2 className="w-3 h-3 animate-spin" />}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const DOC_TYPES = [
  { v: "all", l: "All" },
  { v: "quote_received", l: "Quotes received" },
  { v: "purchase_order", l: "Purchase orders" },
  { v: "sales_order_received", l: "Sales orders (from supplier)" },
  { v: "invoice_received", l: "Invoices" },
  { v: "credit_note", l: "Credit notes" },
  { v: "statement", l: "Statements" },
  { v: "contract", l: "Contracts" },
  { v: "sla", l: "SLA" },
  { v: "credit_application", l: "Credit application" },
  { v: "warranty", l: "Warranty" },
  { v: "bee_certificate", l: "BEE certificate" },
  { v: "brochure", l: "Brochures / catalogues" },
  { v: "price_list", l: "Price lists" },
  { v: "spec_sheet", l: "Spec sheets" },
  { v: "photo", l: "Photos" },
  { v: "video", l: "Videos" },
  { v: "other", l: "Other" },
];

// Iter 418.g (Dirk, 2026-02-28) — Per-tab doc_type filter maps mirror
// the customer-page split between contractual/legal (Documents) and
// transactional (Account). Media = photo/video only.
const ACCOUNT_DOC_TYPES = [
  { v: "all", l: "All" },
  { v: "quote_received", l: "Quotes received" },
  { v: "sales_order_received", l: "Sales orders / Proforma" },
  { v: "purchase_order", l: "Purchase orders" },
  { v: "invoice_received", l: "Supplier invoices" },
  { v: "credit_note", l: "Credit notes" },
  { v: "statement", l: "Statements" },
];
const DOCS_DOC_TYPES = [
  { v: "all", l: "All" },
  { v: "contract", l: "Contracts" },
  { v: "sla", l: "SLA" },
  { v: "credit_application", l: "Credit application" },
  { v: "warranty", l: "Warranty" },
  { v: "bee_certificate", l: "BEE certificate" },
  { v: "brochure", l: "Brochures / catalogues" },
  { v: "price_list", l: "Price lists" },
  { v: "spec_sheet", l: "Spec sheets" },
  { v: "other", l: "Other" },
];
const MEDIA_DOC_TYPES = [
  { v: "all", l: "All" },
  { v: "photo", l: "Photos" },
  { v: "video", l: "Videos" },
];

function DocumentsBlock({ supplier, canEdit, types = DOC_TYPES, defaultUploadType, emptyLabel }) {
  const TYPES = types;
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [uploadType, setUploadType] = useState(defaultUploadType || (TYPES.find((t) => t.v !== "all")?.v ?? "other"));
  const [uploadName, setUploadName] = useState("");
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? "" : `?doc_type=${filter}`;
      const { data } = await api.get(`/suppliers/${supplier.id}/documents${params}`);
      // Iter 418.g — client-side filter to just the doc_types this tab
      // is responsible for, so the Account tab doesn't leak contracts
      // and vice-versa.
      const allowedTypes = new Set(TYPES.filter((t) => t.v !== "all").map((t) => t.v));
      const filtered = (data.items || []).filter((d) => allowedTypes.has(d.doc_type || "other"));
      setDocs(filtered);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load."); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter, supplier.id, types]);

  const onPick = () => fileRef.current?.click();

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("doc_type", uploadType);
        if (uploadName) fd.append("name", uploadName);
        await api.post(`/suppliers/${supplier.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} file${files.length === 1 ? "" : "s"} uploaded.`);
      setUploadName("");
      e.target.value = "";
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  const remove = async (did) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/suppliers/${supplier.id}/documents/${did}`);
      setDocs((p) => p.filter((d) => d.id !== did));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  const downloadUrl = (d) => {
    const tok = localStorage.getItem("hv_token");
    return `${BACKEND_URL}/api/uploads/${d.upload_id}?auth=${encodeURIComponent(tok || "")}`;
  };

  return (
    <div className="space-y-3">
      {canEdit && (
        <div className="hv-card p-3 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1]">Upload</div>
          <div className="grid sm:grid-cols-3 gap-2 items-end">
            <label className="block sm:col-span-1">
              <span className="text-[11px] text-[#a0aabf]">Document type</span>
              <select value={uploadType} onChange={(e) => setUploadType(e.target.value)} className="hv-input text-sm w-full" data-testid="supplier-upload-type">
                {TYPES.filter((t) => t.v !== "all").map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </label>
            <label className="block sm:col-span-1">
              <span className="text-[11px] text-[#a0aabf]">Custom name (optional)</span>
              <input value={uploadName} onChange={(e) => setUploadName(e.target.value)} className="hv-input text-sm w-full" placeholder="Defaults to filename" />
            </label>
            <div>
              <input ref={fileRef} type="file" multiple onChange={onUpload} className="hidden" />
              <button onClick={onPick} disabled={uploading} className="hv-btn hv-btn-primary text-sm w-full" data-testid="supplier-upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Upload file(s)
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-1 flex-wrap">
        {TYPES.map((t) => (
          <button key={t.v} onClick={() => setFilter(t.v)} className={`text-[11px] px-2 py-1 rounded border transition ${filter === t.v ? "border-[#1FB6C1] bg-[#1FB6C1]/[0.07] text-[#1FB6C1]" : "border-white/10 hover:border-[#1FB6C1]/40 text-[#cbd2df]"}`} data-testid={`supplier-doc-filter-${t.v}`}>
            {t.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-6 text-[#647087]"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : docs.length === 0 ? (
        <div className="hv-card p-6 text-center text-sm text-[#a0aabf]">{emptyLabel || "No documents in this category yet."}</div>
      ) : filter === "all" ? (
        <FolderedDocs
          docs={docs}
          DOC_TYPES={TYPES}
          renderRow={(d) => (
            <DocRow
              key={d.id}
              d={d}
              supplier={supplier}
              canEdit={canEdit}
              refresh={refresh}
              remove={remove}
              downloadUrl={downloadUrl}
              DOC_TYPES={TYPES}
            />
          )}
        />
      ) : (
        <div className="space-y-1.5">
          {docs.map((d) => (
            <DocRow
              key={d.id}
              d={d}
              supplier={supplier}
              canEdit={canEdit}
              refresh={refresh}
              remove={remove}
              downloadUrl={downloadUrl}
              DOC_TYPES={TYPES}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Iter 411 (Dirk, 2026-02-28) — Extracted doc row so the same markup
// serves the flat filtered list AND the collapsible per-type folders.
function DocRow({ d, supplier, canEdit, refresh, remove, downloadUrl, DOC_TYPES }) {
  // Iter 411 (Dirk, 2026-02-28) — Mobile-safe stack. On < md the doc
  // name gets its own row (full width, truncated with word-break-word
  // rather than character wraps), badges wrap on their own row, and
  // action buttons fill the last row. On md+ everything stays inline.
  return (
    <div className="hv-card p-2.5 flex flex-col md:flex-row md:items-center gap-2" data-testid={`supplier-doc-${d.id}`}>
      <div className="flex items-start gap-2 min-w-0 flex-1">
        <FileText className="w-4 h-4 text-[#1FB6C1] shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div
            className="text-sm font-bold break-all md:truncate"
            title={d.name}
            style={{ wordBreak: "break-word" }}
          >
            {d.name}
          </div>
          <div className="text-[10px] text-[#a0aabf] break-words">
            {DOC_TYPES.find((t) => t.v === d.doc_type)?.l || d.doc_type}
            {d.size_bytes ? ` · ${Math.round(d.size_bytes / 1024)} KB` : ""}
            {d.uploaded_by_name ? ` · by ${d.uploaded_by_name}` : ""}
            {d.document_number ? ` · ${d.document_number}` : ""}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap md:flex-nowrap">
        {d.ocr_status && (
          <OcrBadge doc={d} supplierId={supplier.id} onRefresh={refresh} />
        )}
        {d.ocr_status === "ready" && (d.line_items || []).length > 0 && (
          <SendToTrackerButton supplier={supplier} doc={d} />
        )}
        {d.ocr_status && d.ocr_status !== "none" && (
          <Link
            to={`/ocr/supplier-invoices?supplier_id=${supplier.id}&doc=${d.id}`}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid={`supplier-doc-scanner-${d.id}`}
            title="Open full scanner (edit lines / match inventory)"
          >
            <FileText className="w-3.5 h-3.5" />
            Scanner
          </Link>
        )}
        {canEdit && (
          <select
            value={d.doc_type || "other"}
            onChange={async (e) => {
              const newType = e.target.value;
              if (newType === d.doc_type) return;
              try {
                await api.patch(`/suppliers/${supplier.id}/documents/${d.id}`, { doc_type: newType });
                toast.success(`Moved to "${DOC_TYPES.find((t) => t.v === newType)?.l || newType}".`);
                refresh();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Move failed.");
              }
            }}
            className="bg-black/40 border border-white/10 rounded px-1.5 py-1 text-[10px] text-[#cbd2df] hover:border-[#1FB6C1]/40 focus:border-[#1FB6C1]/60 focus:outline-none max-w-[9rem]"
            data-testid={`supplier-doc-move-${d.id}`}
            title="Move this document to a different category"
          >
            {DOC_TYPES.filter((t) => t.v !== "all").map((t) => (
              <option key={t.v} value={t.v}>{t.l}</option>
            ))}
          </select>
        )}
        <a href={downloadUrl(d)} target="_blank" rel="noreferrer" className="hv-btn hv-btn-ghost text-xs" data-testid={`supplier-doc-dl-${d.id}`} title="Download">
          <Download className="w-3.5 h-3.5" />
        </a>
        {canEdit && (
          <button onClick={() => remove(d.id)} className="hv-btn hv-btn-ghost text-xs" title="Delete" data-testid={`supplier-doc-del-${d.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// Iter 411 (Dirk, 2026-02-28) — Folder view of documents grouped by
// doc_type. Collapsible sections mirroring the Customer Accounts
// grouped-by-category layout. Each section header shows count + how
// many docs are OCR-ready with reconciliation status.
function FolderedDocs({ docs, DOC_TYPES, renderRow }) {
  const grouped = {};
  for (const d of docs) {
    const k = d.doc_type || "other";
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(d);
  }
  // Order: use DOC_TYPES order (skip "all"), then any remaining unknown types.
  const orderedTypes = DOC_TYPES.filter((t) => t.v !== "all").map((t) => t.v);
  const extras = Object.keys(grouped).filter((k) => !orderedTypes.includes(k));
  const finalOrder = [...orderedTypes, ...extras].filter((k) => grouped[k]?.length > 0);

  return (
    <div className="space-y-3" data-testid="supplier-docs-folders">
      {finalOrder.map((k) => (
        <SupplierDocFolder
          key={k}
          typeKey={k}
          label={DOC_TYPES.find((t) => t.v === k)?.l || k}
          docs={grouped[k]}
          renderRow={renderRow}
        />
      ))}
    </div>
  );
}

function SupplierDocFolder({ typeKey, label, docs, renderRow }) {
  const [open, setOpen] = useState(true);
  const okCount = docs.filter((d) => d.ocr_status === "ready").length;
  const flagged = docs.filter((d) => d?.ocr_reconciliation?.status === "mismatch").length;
  return (
    <div className="hv-card p-0 overflow-hidden" data-testid={`supplier-doc-folder-${typeKey}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/[0.02] transition text-left"
        data-testid={`supplier-doc-folder-toggle-${typeKey}`}
      >
        <FileText className="w-4 h-4 text-[#1FB6C1] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold">{label}</div>
          <div className="text-[10px] text-[#647087]">
            {docs.length} document{docs.length === 1 ? "" : "s"}
            {okCount > 0 ? ` · ${okCount} OCR ready` : ""}
            {flagged > 0 ? ` · ${flagged} need review` : ""}
          </div>
        </div>
        <span className="text-[10px] text-[#647087]">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="p-2 space-y-1.5 border-t border-white/5">
          {docs.map((d) => renderRow(d))}
        </div>
      )}
    </div>
  );
}


function InventoryBlock({ supplier }) {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("name");
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [s, inv] = await Promise.all([
          api.get(`/suppliers/${supplier.id}/inventory-stats`).catch(() => ({ data: null })),
          api.get(`/suppliers/${supplier.id}/inventory?sort=${sort}&limit=300`),
        ]);
        setStats(s.data);
        setRows(inv.data.items || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load.");
      } finally { setLoading(false); }
    })();
  }, [supplier.id, sort]);

  if (loading) return <div className="flex justify-center py-6 text-[#647087]"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const empty = !rows.length && (!stats || !stats.item_count);

  const fmtZar = (v) => `R ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-3" data-testid="supplier-inventory-block">
      {/* Price-list import (PDF/Excel/CSV → OCR/parse → match-first review) */}
      <SupplierPricelistUploader
        supplierId={supplier.id}
        onCommitted={() => {
          // Toggle sort to itself to re-trigger the useEffect data refresh
          setSort((s) => s === "name" ? "name " : "name");
          setTimeout(() => setSort("name"), 200);
        }}
      />

      {empty ? (
        <div className="hv-card p-6 text-center text-sm text-[#a0aabf]">
          No inventory linked to this supplier yet. Upload a price list above, or use &quot;Link suppliers&quot; on the Inventory tab to auto-link by supplier code.
        </div>
      ) : (
        <>
          {/* Stats strip */}
      {stats && stats.item_count > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <StatTile label="Items" value={stats.item_count.toLocaleString()} />
          <StatTile label="On-hand value" value={fmtZar(stats.total_on_hand_value)} accent="text-emerald-300" />
          <StatTile label="Avg GP" value={stats.avg_gp != null ? `${stats.avg_gp.toFixed(1)}%` : "—"} accent={stats.avg_gp >= 25 ? "text-emerald-300" : "text-amber-300"} />
          <StatTile label="Low stock" value={stats.low_stock.length} accent={stats.low_stock.length ? "text-rose-300" : "text-[#647087]"} />
        </div>
      )}

      {/* Top sellers / Low stock / Dormant chips */}
      {stats && (stats.top_sellers.length || stats.low_stock.length || stats.dormant.length) > 0 && (
        <div className="grid sm:grid-cols-3 gap-2">
          <MiniList title="Top sellers (90 d)" emptyText="No sales recorded yet." rows={stats.top_sellers} render={(r) => (
            <span><b className="text-emerald-300">{r.units_90d.toFixed(0)}</b> · {r.name}</span>
          )} />
          <MiniList title="Low stock" emptyText="Everything above reorder level." rows={stats.low_stock} render={(r) => (
            <span><b className="text-rose-300">{r.on_hand}</b>/{r.reorder_level} · {r.name}</span>
          )} />
          <MiniList title="Dormant (no sale 90 d)" emptyText="No idle stock." rows={stats.dormant.slice(0, 5)} render={(r) => (
            <span>{r.name} · {r.on_hand} × {fmtZar(r.value / Math.max(r.on_hand, 1))}</span>
          )} />
        </div>
      )}

      {/* Sort control */}
      <div className="flex items-center gap-2 text-[11px]">
        <span className="text-[#647087]">Sort:</span>
        {[
          { v: "name", l: "Name" },
          { v: "on_hand", l: "On hand" },
          { v: "gp", l: "GP%" },
          { v: "sales_90d", l: "Sales (90 d)" },
          { v: "last_sale", l: "Last sold" },
        ].map((o) => (
          <button
            key={o.v}
            onClick={() => setSort(o.v)}
            className={"px-2 py-0.5 rounded transition " + (sort === o.v ? "bg-[#1FB6C1]/20 text-[#1FB6C1]" : "text-[#9aa3b5] hover:text-white")}
            data-testid={`supplier-inv-sort-${o.v}`}
          >
            {o.l}
          </button>
        ))}
      </div>

      {/* Compact rows */}
      <div className="space-y-1">
        {rows.map((r) => {
          const sell = Number(r.sell_price_incl ?? r.sell_price_excl);
          const gp = Number(r.gross_margin_pct);
          return (
            <Link key={r.id} to={`/equipment?id=${r.id}`} className="hv-card p-2 flex items-center gap-2 hover:border-[#1FB6C1]/40 transition" data-testid={`supplier-inv-${r.id}`}>
              <Package className="w-4 h-4 text-[#1FB6C1] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate text-white">{r.name || "(unnamed item)"}</div>
                <div className="text-[10px] text-[#a0aabf] flex items-center gap-2 flex-wrap">
                  {r.sku && <span className="font-mono">{r.sku}</span>}
                  <span>· on hand <b className={Number(r.on_hand_qty) <= Number(r.reorder_level || 0) ? "text-rose-300" : "text-[#dde3ec]"}>{Number(r.on_hand_qty || 0).toLocaleString()}</b></span>
                  {Number.isFinite(sell) && <span>· {fmtZar(sell)}</span>}
                  {Number.isFinite(gp) && <span>· GP {gp.toFixed(1)}%</span>}
                  {Number(r.units_sold_90d) > 0 && <span className="text-emerald-300">· {r.units_sold_90d.toFixed(0)} sold 90d</span>}
                  {r.last_sale_at && <span className="text-[#647087]">· last sold {new Date(r.last_sale_at).toLocaleDateString()}</span>}
                </div>
              </div>
            </Link>
          );
          })}
        </div>
        </>
      )}
    </div>
  );
}

function StatTile({ label, value, accent = "text-white" }) {
  return (
    <div className="hv-card p-2 text-center">
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#647087]">{label}</div>
      <div className={"text-sm font-bold mt-0.5 " + accent}>{value}</div>
    </div>
  );
}

function MiniList({ title, rows, render, emptyText }) {
  return (
    <div className="hv-card p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1.5">{title}</div>
      {rows.length === 0 ? (
        <div className="text-[10px] text-[#647087] italic">{emptyText}</div>
      ) : (
        <ul className="space-y-0.5 text-[10px]">
          {rows.slice(0, 5).map((r) => (
            <li key={r.id} className="truncate text-[#dde3ec]" title={r.name}>{render(r)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DatasheetsBlock({ supplier }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/suppliers/${supplier.id}/datasheets`);
        setRows(data.items || []);
      } catch (e) { /* tolerate datasheets module not yet built */ }
      finally { setLoading(false); }
    })();
  }, [supplier.id]);

  if (loading) return <div className="flex justify-center py-6 text-[#647087]"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (rows.length === 0) return <div className="hv-card p-6 text-center text-sm text-[#a0aabf]">No datasheets linked to this supplier yet. The Datasheets library (coming in 25.2) will surface them here.</div>;
  return (
    <div className="space-y-1.5">
      {rows.map((r) => (
        <Link key={r.id} to={`/equipment/datasheets/${r.id}`} className="hv-card p-2.5 flex items-center gap-2 hover:border-[#1FB6C1]/40 transition">
          <BookOpen className="w-4 h-4 text-[#1FB6C1] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold truncate">{r.name}</div>
            <div className="text-[10px] text-[#a0aabf]">{r.category || ""}</div>
          </div>
        </Link>
      ))}
    </div>
  );
}

const STATUS_TINTS = {
  active: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  preferred: "bg-[#1FB6C1]/10 text-[#1FB6C1] border-[#1FB6C1]/30",
  inactive: "bg-white/5 text-[#a0aabf] border-white/10",
  blocked: "bg-rose-500/10 text-rose-300 border-rose-500/30",
};

export default function SupplierDetail() {
  const { sid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});

  const canEdit = ["owner", "admin", "manager"].includes(user?.role);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/suppliers/${sid}`);
        setSupplier(data); setDraft(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Supplier not found.");
        nav("/suppliers");
      } finally { setLoading(false); }
    })();
  }, [sid, nav]);

  const save = async () => {
    try {
      const { data } = await api.patch(`/suppliers/${sid}`, draft);
      setSupplier(data); setDraft(data); setEditing(false);
      toast.success("Saved.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this supplier? If it has linked records it will be marked inactive instead.")) return;
    try {
      const { data } = await api.delete(`/suppliers/${sid}`);
      toast.success(data.soft_deleted ? "Marked inactive — has linked records." : "Deleted.");
      nav("/suppliers");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  if (loading || !supplier) {
    return <div className="flex items-center justify-center py-20 text-[#647087]"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="supplier-detail">
      {/* Breadcrumb + header */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#a0aabf]">
          <Link to="/suppliers" className="hover:text-[#1FB6C1]">Suppliers</Link>
          <span className="opacity-40">/</span>
          <span className="text-[#1FB6C1]">{supplier.external_ref}</span>
        </div>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-md bg-[#1FB6C1]/15 text-[#1FB6C1] flex items-center justify-center shrink-0">
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{supplier.name}</h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {supplier.status && (
                  <span className={`text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border ${STATUS_TINTS[supplier.status] || STATUS_TINTS.inactive}`}>
                    {supplier.status}
                  </span>
                )}
                {supplier.supplier_type && <span className="text-[11px] text-[#a0aabf] capitalize">{supplier.supplier_type}</span>}
                {supplier.country && <span className="text-[11px] text-[#a0aabf]">· {supplier.country}</span>}
              </div>
            </div>
          </div>
          {canEdit && (
            <div className="flex gap-1">
              {!editing ? (
                <button onClick={() => setEditing(true)} className="hv-btn hv-btn-ghost text-xs" data-testid="supplier-edit"><Edit3 className="w-3.5 h-3.5" /> Edit</button>
              ) : (
                <>
                  <button onClick={() => { setDraft(supplier); setEditing(false); }} className="hv-btn hv-btn-ghost text-xs">Cancel</button>
                  <button onClick={save} className="hv-btn hv-btn-primary text-xs" data-testid="supplier-save"><Save className="w-3.5 h-3.5" /> Save</button>
                </>
              )}
              <button onClick={remove} className="hv-btn hv-btn-ghost text-xs" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs — Iter 418.g (Dirk, 2026-02-28) — Full parity with customer detail.
          Overview · Media · Documents (contracts/legal) · Account
          (transactional) · Orders · Timeline · Inventory · Datasheets. */}
      <div className="flex items-center gap-1 border-b border-white/10 overflow-x-auto">
        {[
          { k: "overview", label: "Overview" },
          { k: "media", label: "Media" },
          { k: "documents", label: "Documents" },
          { k: "account", label: "Account" },
          { k: "orders", label: "Orders" },
          { k: "timeline", label: "Timeline" },
          { k: "inventory", label: "Inventory" },
          { k: "datasheets", label: "Datasheets" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-3 py-2 text-xs font-bold uppercase tracking-[0.15em] border-b-2 transition shrink-0 ${tab === t.k ? "border-[#1FB6C1] text-[#1FB6C1]" : "border-transparent text-[#a0aabf] hover:text-white"}`} data-testid={`supplier-tab-${t.k}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-3">
          <div className="hv-card p-4 grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1]">Contact</div>
              {supplier.primary_email && <div className="text-sm flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#a0aabf]" /> <a href={`mailto:${supplier.primary_email}`} className="hover:text-[#1FB6C1]" data-testid="supplier-primary-email">{supplier.primary_email}</a></div>}
              {supplier.secondary_email && <div className="text-sm flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#a0aabf] opacity-60" /> <a href={`mailto:${supplier.secondary_email}`} className="hover:text-[#1FB6C1]" data-testid="supplier-secondary-email">{supplier.secondary_email}</a><span className="text-[10px] text-[#647087] uppercase tracking-wider">cc</span></div>}
              {supplier.primary_phone && <div className="text-sm flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#a0aabf]" /> <a href={`tel:${supplier.primary_phone}`} className="hover:text-[#1FB6C1]" data-testid="supplier-primary-phone">{supplier.primary_phone}</a></div>}
              {supplier.mobile_phone && <div className="text-sm flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#a0aabf] opacity-60" /> <a href={`tel:${supplier.mobile_phone}`} className="hover:text-[#1FB6C1]" data-testid="supplier-mobile-phone">{supplier.mobile_phone}</a><span className="text-[10px] text-[#647087] uppercase tracking-wider">mobile</span></div>}
              {supplier.website && <div className="text-sm flex items-center gap-2"><Globe className="w-3.5 h-3.5 text-[#a0aabf]" /> <a href={supplier.website} target="_blank" rel="noreferrer" className="hover:text-[#1FB6C1] truncate">{supplier.website}</a></div>}
              {supplier.physical_address && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(supplier.physical_address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm flex items-start gap-2 group hover:text-[#1FB6C1] transition-colors"
                  title="Open in Google Maps"
                  data-testid="supplier-physical-address"
                >
                  <MapPin className="w-3.5 h-3.5 text-[#a0aabf] mt-0.5 group-hover:text-[#1FB6C1]" />
                  <span className="underline decoration-dotted decoration-[#a0aabf]/40 underline-offset-4 group-hover:decoration-[#1FB6C1]">
                    {supplier.physical_address}
                  </span>
                </a>
              )}
              {supplier.billing_address && supplier.billing_address !== supplier.physical_address && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(supplier.billing_address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm flex items-start gap-2 group hover:text-[#1FB6C1] transition-colors"
                  title="Open postal address in Google Maps"
                  data-testid="supplier-billing-address"
                >
                  <MapPin className="w-3.5 h-3.5 text-[#a0aabf]/60 mt-0.5 group-hover:text-[#1FB6C1]" />
                  <span className="underline decoration-dotted decoration-[#a0aabf]/40 underline-offset-4 group-hover:decoration-[#1FB6C1]">
                    {supplier.billing_address}
                  </span>
                  <span className="text-[10px] text-[#647087] uppercase tracking-wider mt-1">postal</span>
                </a>
              )}
            </div>
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1]">Trading terms</div>
              <div className="text-sm">{supplier.payment_terms || "—"}{supplier.payment_terms === "custom" && supplier.payment_terms_custom ? `: ${supplier.payment_terms_custom}` : ""}</div>
              {supplier.lead_time_days && <div className="text-xs text-[#a0aabf]">Lead time: {supplier.lead_time_days} days</div>}
              {supplier.currency && <div className="text-xs text-[#a0aabf]">Currency: {supplier.currency}</div>}
              {supplier.our_account_number && <div className="text-xs text-[#a0aabf]">Our acct #: {supplier.our_account_number}</div>}
              {supplier.credit_limit != null && <div className="text-xs text-[#a0aabf]">Credit limit: {supplier.credit_limit}</div>}
              {supplier.vat_number && <div className="text-xs text-[#a0aabf]">VAT: {supplier.vat_number}</div>}
              {supplier.registration_number && <div className="text-xs text-[#a0aabf]">Reg #: {supplier.registration_number}</div>}
              {/* Iter 418.g — parity extensions. */}
              {supplier.min_order_value != null && <div className="text-xs text-[#a0aabf]">Min order value: R {Number(supplier.min_order_value).toLocaleString()}</div>}
              {supplier.discount_tier && <div className="text-xs text-[#a0aabf]">Discount tier: <span className="text-[#1FB6C1]">{supplier.discount_tier}</span></div>}
              {supplier.general_ledger_code && <div className="text-xs text-[#a0aabf]">GL code: {supplier.general_ledger_code}</div>}
              {supplier.preferred_delivery_method && <div className="text-xs text-[#a0aabf] capitalize">Delivery: {supplier.preferred_delivery_method.replace(/_/g, " ")}</div>}
            </div>
          </div>

          {/* Iter 418.g — Bank details card. Only rendered when at least one bank field is filled. */}
          {(supplier.bank_name || supplier.bank_account_number || supplier.bank_branch_code || supplier.bank_swift_code) && (
            <div className="hv-card p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1] mb-2">Bank details</div>
              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                {supplier.bank_name && <div><span className="text-[#647087] text-xs mr-2">Bank</span>{supplier.bank_name}</div>}
                {supplier.bank_account_number && <div><span className="text-[#647087] text-xs mr-2">Account #</span><span className="font-mono">{supplier.bank_account_number}</span></div>}
                {supplier.bank_branch_code && <div><span className="text-[#647087] text-xs mr-2">Branch</span><span className="font-mono">{supplier.bank_branch_code}</span></div>}
                {supplier.bank_swift_code && <div><span className="text-[#647087] text-xs mr-2">SWIFT</span><span className="font-mono">{supplier.bank_swift_code}</span></div>}
              </div>
            </div>
          )}

          {supplier.tags?.length > 0 && (
            <div className="hv-card p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1] mb-2 flex items-center gap-1"><Tag className="w-3 h-3" /> Categories</div>
              <div className="flex flex-wrap gap-1.5">
                {supplier.tags.map((t) => (
                  <span key={t} className="text-[11px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-[#cbd2df]">{t}</span>
                ))}
              </div>
            </div>
          )}

          {supplier.contacts?.length > 0 && (
            <div className="hv-card p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1] mb-2">Contacts ({supplier.contacts.length})</div>
              <div className="space-y-1.5">
                {supplier.contacts.map((c, i) => (
                  <div key={i} className="text-sm flex items-center gap-2 flex-wrap">
                    <span className="font-bold">{c.name}</span>
                    {c.role && <span className="text-[#a0aabf]">— {c.role}</span>}
                    {c.email && <a href={`mailto:${c.email}`} className="text-[#1FB6C1] text-xs">{c.email}</a>}
                    {c.phone && <a href={`tel:${c.phone}`} className="text-xs text-[#a0aabf]">{c.phone}</a>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {supplier.notes && (
            <div className="hv-card p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1] mb-2">Notes</div>
              <div className="text-sm whitespace-pre-wrap">{supplier.notes}</div>
            </div>
          )}
        </div>
      )}

      {/* Iter 418.g — Inline edit form. Shown when the user clicks Edit
          (top-right of the header). Only rendered on the Overview tab
          so a user editing supplier metadata doesn't see the form
          bleed through when they flip to Orders/Timeline/etc. */}
      {editing && tab === "overview" && (
        <SupplierEditForm draft={draft} setDraft={setDraft} />
      )}

      {tab === "media" && (
        <DocumentsBlock
          supplier={supplier}
          canEdit={canEdit}
          types={MEDIA_DOC_TYPES}
          defaultUploadType="photo"
          emptyLabel="No photos or videos uploaded yet."
        />
      )}
      {tab === "documents" && (
        <DocumentsBlock
          supplier={supplier}
          canEdit={canEdit}
          types={DOCS_DOC_TYPES}
          defaultUploadType="contract"
          emptyLabel="No contracts, SLAs or reference documents in this category yet."
        />
      )}
      {tab === "account" && (
        <DocumentsBlock
          supplier={supplier}
          canEdit={canEdit}
          types={ACCOUNT_DOC_TYPES}
          defaultUploadType="purchase_order"
          emptyLabel="No account documents in this category yet."
        />
      )}
      {tab === "orders" && <OrdersBlock supplier={supplier} />}
      {tab === "timeline" && <TimelineBlock supplier={supplier} />}
      {tab === "inventory" && <InventoryBlock supplier={supplier} />}
      {tab === "datasheets" && <DatasheetsBlock supplier={supplier} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Iter 418.g (Dirk, 2026-02-28) — Supplier edit form, orders,
// and timeline blocks. Follow the same UX conventions as the
// customer-detail equivalents so a user switching between the
// two pages sees the same layout and interaction model.
// ─────────────────────────────────────────────────────────────
function SupplierEditForm({ draft, setDraft }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setContact = (i, patch) => {
    const contacts = [...(draft.contacts || [])];
    contacts[i] = { ...(contacts[i] || {}), ...patch };
    set("contacts", contacts);
  };
  const addContact = () => set("contacts", [...(draft.contacts || []), { name: "", role: "", email: "", phone: "" }]);
  const removeContact = (i) => set("contacts", (draft.contacts || []).filter((_, ix) => ix !== i));
  const tagsStr = (draft.tags || []).join(", ");
  return (
    <div className="hv-card p-4 space-y-4" data-testid="supplier-edit-form">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1]">Edit supplier</div>

      {/* Identity + status */}
      <div className="grid sm:grid-cols-2 gap-3">
        <FieldText label="Name" value={draft.name} onChange={(v) => set("name", v)} testId="s-edit-name" />
        <FieldSelect
          label="Status"
          value={draft.status || "active"}
          options={["active", "preferred", "inactive", "blocked"]}
          onChange={(v) => set("status", v)}
        />
        <FieldSelect
          label="Supplier type"
          value={draft.supplier_type || "distributor"}
          options={["manufacturer", "distributor", "service", "agent", "oem"]}
          onChange={(v) => set("supplier_type", v)}
        />
        <FieldText label="Country" value={draft.country} onChange={(v) => set("country", v)} />
        <FieldText label="Registration #" value={draft.registration_number} onChange={(v) => set("registration_number", v)} />
        <FieldText label="VAT #" value={draft.vat_number} onChange={(v) => set("vat_number", v)} />
        <FieldText label="Website" value={draft.website} onChange={(v) => set("website", v)} />
      </div>

      {/* Contact */}
      <div className="grid sm:grid-cols-2 gap-3">
        <FieldText label="Primary email" value={draft.primary_email} onChange={(v) => set("primary_email", v)} type="email" testId="s-edit-primary-email" />
        <FieldText label="Secondary email (CC)" value={draft.secondary_email} onChange={(v) => set("secondary_email", v)} type="email" />
        <FieldText label="Primary phone" value={draft.primary_phone} onChange={(v) => set("primary_phone", v)} />
        <FieldText label="Mobile phone" value={draft.mobile_phone} onChange={(v) => set("mobile_phone", v)} />
        <FieldText label="Physical address" value={draft.physical_address} onChange={(v) => set("physical_address", v)} className="sm:col-span-2" />
        <FieldText label="Postal / billing address" value={draft.billing_address} onChange={(v) => set("billing_address", v)} className="sm:col-span-2" />
      </div>

      {/* Trading terms */}
      <div className="grid sm:grid-cols-2 gap-3">
        <FieldSelect
          label="Payment terms"
          value={draft.payment_terms || ""}
          options={["", "COD", "7d", "14d", "30d", "60d", "custom"]}
          onChange={(v) => set("payment_terms", v || null)}
        />
        {draft.payment_terms === "custom" && (
          <FieldText label="Payment terms (custom)" value={draft.payment_terms_custom} onChange={(v) => set("payment_terms_custom", v)} />
        )}
        <FieldText label="Currency" value={draft.currency || "ZAR"} onChange={(v) => set("currency", v)} />
        <FieldText label="Lead time (days)" value={draft.lead_time_days ?? ""} onChange={(v) => set("lead_time_days", v === "" ? null : Number(v))} type="number" />
        <FieldText label="Our account #" value={draft.our_account_number} onChange={(v) => set("our_account_number", v)} />
        <FieldText label="Credit limit (ZAR)" value={draft.credit_limit ?? ""} onChange={(v) => set("credit_limit", v === "" ? null : Number(v))} type="number" />
        <FieldText label="Min order value (ZAR)" value={draft.min_order_value ?? ""} onChange={(v) => set("min_order_value", v === "" ? null : Number(v))} type="number" testId="s-edit-moq" />
        <FieldText label="Discount tier" value={draft.discount_tier} onChange={(v) => set("discount_tier", v)} placeholder="e.g. Gold, 5%, Preferred" testId="s-edit-discount-tier" />
        <FieldText label="GL code" value={draft.general_ledger_code} onChange={(v) => set("general_ledger_code", v)} />
        <FieldSelect
          label="Preferred delivery"
          value={draft.preferred_delivery_method || ""}
          options={["", "collection", "courier", "rail", "own_transport"]}
          onChange={(v) => set("preferred_delivery_method", v || null)}
        />
      </div>

      {/* Bank details */}
      <div className="pt-2 border-t border-white/5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1] mb-2">Bank details</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <FieldText label="Bank name" value={draft.bank_name} onChange={(v) => set("bank_name", v)} testId="s-edit-bank-name" />
          <FieldText label="Account number" value={draft.bank_account_number} onChange={(v) => set("bank_account_number", v)} />
          <FieldText label="Branch code" value={draft.bank_branch_code} onChange={(v) => set("bank_branch_code", v)} />
          <FieldText label="SWIFT code" value={draft.bank_swift_code} onChange={(v) => set("bank_swift_code", v)} />
        </div>
      </div>

      {/* Contacts editor */}
      <div className="pt-2 border-t border-white/5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#1FB6C1]">Contacts</div>
          <button onClick={addContact} className="hv-btn hv-btn-ghost text-xs" data-testid="s-edit-add-contact">+ Add contact</button>
        </div>
        <div className="space-y-2">
          {(draft.contacts || []).map((c, i) => (
            <div key={i} className="grid sm:grid-cols-5 gap-2 items-end">
              <FieldText label={i === 0 ? "Name" : ""} value={c.name} onChange={(v) => setContact(i, { name: v })} />
              <FieldText label={i === 0 ? "Role" : ""} value={c.role} onChange={(v) => setContact(i, { role: v })} />
              <FieldText label={i === 0 ? "Email" : ""} value={c.email} onChange={(v) => setContact(i, { email: v })} />
              <FieldText label={i === 0 ? "Phone" : ""} value={c.phone} onChange={(v) => setContact(i, { phone: v })} />
              <button onClick={() => removeContact(i)} className="hv-btn hv-btn-ghost text-xs text-rose-300"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {(!draft.contacts || draft.contacts.length === 0) && (
            <div className="text-xs text-[#647087]">No individual contacts yet.</div>
          )}
        </div>
      </div>

      {/* Tags + notes */}
      <div className="pt-2 border-t border-white/5 grid sm:grid-cols-2 gap-3">
        <FieldText
          label="Categories (comma-separated)"
          value={tagsStr}
          onChange={(v) => set("tags", v.split(",").map((s) => s.trim()).filter(Boolean))}
          placeholder="pumps, ro, membranes, dosing"
          className="sm:col-span-2"
        />
        <label className="block sm:col-span-2">
          <span className="text-[11px] text-[#a0aabf]">Notes</span>
          <DictTextarea
            value={draft.notes || ""}
            onChange={(e) => set("notes", e.target.value)}
            rows={3}
            className="hv-input text-sm w-full mt-0.5"
            placeholder="Internal notes about this supplier — quirks, contacts to avoid, past issues, etc."
          />
        </label>
      </div>
    </div>
  );
}

function FieldText({ label, value, onChange, type = "text", placeholder, className = "", testId }) {
  return (
    <label className={`block ${className}`}>
      {label && <span className="text-[11px] text-[#a0aabf]">{label}</span>}
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="hv-input text-sm w-full mt-0.5"
        data-testid={testId}
      />
    </label>
  );
}
function FieldSelect({ label, value, options, onChange }) {
  return (
    <label className="block">
      <span className="text-[11px] text-[#a0aabf]">{label}</span>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="hv-input text-sm w-full mt-0.5">
        {options.map((o) => (
          <option key={o} value={o}>{o === "" ? "—" : o.replace(/_/g, " ")}</option>
        ))}
      </select>
    </label>
  );
}

function OrdersBlock({ supplier }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/suppliers/${supplier.id}/orders`);
        setRows(data.items || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load orders.");
      } finally { setLoading(false); }
    })();
  }, [supplier.id]);
  if (loading) return <div className="flex justify-center py-6 text-[#647087]"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (rows.length === 0) return <div className="hv-card p-6 text-center text-sm text-[#a0aabf]">No orders yet — the first Procurement Tracker or PO for this supplier will land here.</div>;
  const fmtZar = (v) => v != null ? `R ${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—";
  return (
    <div className="space-y-1.5" data-testid="supplier-orders-block">
      {rows.map((r) => (
        <Link
          key={r.id}
          to={r.url || `/suppliers/${supplier.id}?tab=documents`}
          className="hv-card p-3 flex items-center gap-3 hover:border-[#1FB6C1]/40 transition"
          data-testid={`supplier-order-${r.id}`}
        >
          <Package className="w-4 h-4 text-[#1FB6C1] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold truncate">{r.name}</div>
            <div className="text-[10px] text-[#a0aabf] uppercase tracking-wider">
              {r.kind === "procurement_tracker" ? "Procurement" : (r.doc_type || "Document").replace(/_/g, " ")}
              {r.status ? ` · ${r.status}` : ""}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-sm font-mono">{fmtZar(r.total_value)}</div>
            <div className="text-[10px] text-[#647087]">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</div>
          </div>
        </Link>
      ))}
    </div>
  );
}

function TimelineBlock({ supplier }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/suppliers/${supplier.id}/timeline`);
        setEvents(data.items || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load timeline.");
      } finally { setLoading(false); }
    })();
  }, [supplier.id]);
  if (loading) return <div className="flex justify-center py-6 text-[#647087]"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (events.length === 0) return <div className="hv-card p-6 text-center text-sm text-[#a0aabf]">Nothing on the timeline yet. Upload a document or create a PO to see activity here.</div>;
  return (
    <div className="space-y-1.5" data-testid="supplier-timeline-block">
      {events.map((e) => {
        const Icon = e.kind === "document_uploaded" ? FileText : e.kind === "order_created" ? Package : Building2;
        return (
          <div key={e.id} className="hv-card p-3 flex items-start gap-3" data-testid={`supplier-timeline-event-${e.id}`}>
            <div className="mt-0.5 w-6 h-6 rounded bg-[#1FB6C1]/10 text-[#1FB6C1] flex items-center justify-center shrink-0">
              <Icon className="w-3.5 h-3.5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold">{e.title}</div>
              {e.body && <div className="text-xs text-[#a0aabf] mt-0.5 whitespace-pre-wrap">{e.body}</div>}
              <div className="text-[10px] text-[#647087] mt-1">
                {e.at ? new Date(e.at).toLocaleString() : ""}
                {e.actor ? ` · ${e.actor}` : ""}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
